import { useCount } from '@/components/count';
import { DataHeader } from '@/components/dataHeader';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { HeaderInfo, HeaderInfoEdit, HeaderInfoMain } from '@/components/headerComponents';
import { FeatureList, GenericFeature, TaxonomyIcon } from '@/components/highlights';
import PageMetaData from '@/components/PageMetaData';
import { SimpleTooltip } from '@/components/simpleTooltip';
import { Tabs } from '@/components/tabs';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/largeCard';
import { NotFoundError } from '@/errors';
import { SlowTaxonQuery, TaxonKeyQuery } from '@/gql/graphql';
import { DynamicLink } from '@/reactRouterPlugins/dynamicLink';
import EntityDrawer from '@/routes/occurrence/search/views/browseList/ListBrowser';
import { ArticleContainer } from '@/routes/resource/key/components/articleContainer';
import { ArticlePreTitle } from '@/routes/resource/key/components/articlePreTitle';
import { ArticleSkeleton } from '@/routes/resource/key/components/articleSkeleton';
import { ArticleTextContainer } from '@/routes/resource/key/components/articleTextContainer';
import { ArticleTitle } from '@/routes/resource/key/components/articleTitle';
import { PageContainer } from '@/routes/resource/key/components/pageContainer';
import { createContext, useMemo } from 'react';
import { MdInfoOutline, MdLink } from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { Outlet } from 'react-router-dom';
import { getTaxonSchema } from '../../../utils/schemaOrg';
import Cites from './Cites';
import { AboutContent, ApiContent } from './help';
import { HeaderImageCarousel } from './sections/SidebarImageCarousel';
import { useIsSpeciesOrBelow } from '@/hooks/taxonomyRankHooks';
import { HelpLine } from '@/components/helpText';
import { IucnTag } from '@/components/identifierTag';
import { TaxonStubClassification } from '@/components/classification';
import AboutNonBackbone from './AboutNonBackbone';
import { apiConstants } from '@/config/apiConstants';
import { isSupportedChecklist } from '@/hooks/useSupportedChecklists';
import { TocLi as Li } from '@/components/TocHelp';
import { Card as SmallCard } from '@/components/ui/smallCard';

// create context to pass data to children
export const TaxonKeyContext = createContext<{
  key?: string;
  data: TaxonKeyQuery;
  slowTaxon?: SlowTaxonQuery;
  slowTaxonLoading?: boolean;
}>({
  data: {
    __typename: undefined,
  },
  slowTaxon: {
    __typename: undefined,
  },
});

export function TaxonKey({
  data,
  slowTaxon,
  slowTaxonLoading,
}: {
  data: TaxonKeyQuery;
  slowTaxon?: SlowTaxonQuery;
  slowTaxonLoading: boolean;
}) {
  if (data.taxonInfo == null) throw new NotFoundError();

  return (
    <PageHeader data={data}>
      <TaxonKeyContext.Provider
        value={{
          data: data,
          slowTaxon: slowTaxon,
          slowTaxonLoading: slowTaxonLoading,
        }}
      >
        <Outlet />
      </TaxonKeyContext.Provider>
    </PageHeader>
  );
}

export const NonBackbonePresentation = ({ data }: { data: TaxonKeyQuery }) => {
  const taxonInfo = data.taxonInfo;
  const checklistbankURL = taxonInfo?.checklistbankURL;
  const relatedColEntry = taxonInfo?.relatedColEntries?.[0];
  const hasSidebarLinks = checklistbankURL || relatedColEntry;

  return (
    <ArticleContainer className="g-bg-slate-100 g-p-0">
      <ArticleTextContainer className="g-max-w-screen-xl">
        {taxonInfo?.datasetKey && (
          <div className="g-flex g-items-center g-gap-2 g-mb-4">
            <Button asChild variant="outline" className="g-flex-none g-bg-white">
              <DynamicLink
                pageId="datasetKey"
                variables={{ key: taxonInfo?.datasetKey }}
                path={`/taxon`}
              >
                <FormattedMessage id="taxon.viewAllTaxa" defaultMessage="View all" />
              </DynamicLink>
            </Button>
          </div>
        )}
        <div className="g-grid g-gap-4 g-grid-cols-1 min-[1000px]:g-grid-cols-[minmax(0,1fr)_300px]">
          <Card className="g-overflow-hidden g-mb-8">
            <AboutNonBackbone data={data} />
          </Card>
          {hasSidebarLinks && (
            <aside>
              <div className="g-sticky g-top-[var(--stickyOffset)] g-pt-4 -g-mt-4">
                <SmallCard>
                  <nav>
                    <ul className="g-list-none g-m-0 g-p-0 g-my-2">
                      {relatedColEntry && (
                        <Li>
                          <DynamicLink
                            className="g-text-inherit"
                            pageId="taxonKey"
                            variables={{ key: relatedColEntry.taxonID }}
                          >
                            <FormattedMessage
                              id="taxon.viewInDefaultTaxonomy"
                              defaultMessage="View in GBIF taxonomy"
                            />
                          </DynamicLink>
                        </Li>
                      )}
                      {checklistbankURL && (
                        <Li href={checklistbankURL}>
                          <FormattedMessage id="taxon.viewInChecklistBank" />
                          <MdLink className="g-inline g-ms-1" />
                        </Li>
                      )}
                    </ul>
                  </nav>
                </SmallCard>
              </div>
            </aside>
          )}
        </div>
      </ArticleTextContainer>
    </ArticleContainer>
  );
};

const SectionTabs = ({ occurrenceCount = 0 }: { occurrenceCount?: number }) => {
  const tabs = useMemo<{ to: string; children: React.ReactNode }[]>(() => {
    const tabsToDisplay: { to: string; children: React.ReactNode }[] = [
      { to: '.', children: <FormattedMessage id="taxon.tabs.about" /> },
    ];
    if (occurrenceCount > 0) {
      tabsToDisplay.push({
        to: 'metrics',
        children: <FormattedMessage id="taxon.tabs.metrics" defaultMessage="Metrics" />,
      });
    }

    return tabsToDisplay;
  }, [occurrenceCount]);

  return <Tabs links={tabs} />;
};

const PageHeader = ({ data, children }: { data: TaxonKeyQuery; children?: React.ReactNode }) => {
  const { taxonInfo } = data;
  const taxon = taxonInfo;
  if (!taxon) throw new NotFoundError();

  const isIndexedChecklist = isSupportedChecklist(taxonInfo.datasetKey);
  const {
    count,
    loading: countLoading,
    error: countError,
  } = useCount({
    apiEndpoint: apiConstants.occurrenceSearch,
    params: { taxonKey: taxonInfo?.taxonID, checklistKey: taxonInfo.datasetKey },
  });

  const { count: speciesCount } = useCount({
    apiEndpoint: `${apiConstants.taxonApi}/search/${taxonInfo.datasetKey}`,
    params: {
      taxonId: taxonInfo?.taxonID,
      taxonRank: 'SPECIES',
      taxonomicStatus: 'ACCEPTED',
    },
  });

  const isSpeciesOrBelow = useIsSpeciesOrBelow(taxon.taxonRank);
  const hasOccurrenceImages = (taxon?.occurrenceMedia?.count ?? 0) > 0;

  const kingdom = taxonInfo?.classification?.find((c) => c.taxonRank === 'KINGDOM')?.scientificName;
  return (
    <>
      <PageMetaData
        title={taxon.scientificName}
        jsonLd={getTaxonSchema(data)}
        path={`/taxon/${taxonInfo?.taxonID}`} // TODO what about locale here?
        noCanonical
      />

      <EntityDrawer />

      <DataHeader
        className="g-bg-white"
        aboutContent={<AboutContent />}
        apiContent={<ApiContent id={taxon.taxonID} />}
      />

      <article>
        <PageContainer topPadded hasDataHeader className="g-bg-white">
          <ArticleTextContainer className="g-max-w-screen-xl">
            <div className="g-flex">
              {/* Desktop-only left image column */}
              <div className="g-hidden sm:g-block g-flex-none g-self-start g-me-12 g-w-[250px] xl:g-w-[300px]">
                {hasOccurrenceImages && <HeaderImageCarousel taxon={taxon} />}
                {!hasOccurrenceImages && (
                  <div
                    className="g-relative g-w-full g-bg-neutral-100 g-rounded g-overflow-hidden"
                    style={{ paddingBottom: '75%' }}
                  >
                    <div className="g-absolute g-inset-0 g-flex g-flex-col g-items-center g-justify-center">
                      <div className="g-flex g-items-center g-justify-center g-w-full g-h-full">
                        <img
                          src={taxonInfo.groupIconSVG ?? undefined}
                          alt=""
                          className="g-opacity-60"
                          style={{
                            maxWidth: '50%',
                            height: '100%',
                            maxHeight: '50%',
                            display: 'block',
                            objectFit: 'contain',
                          }}
                        />
                      </div>
                      <div className="g-absolute g-bottom-1 g-mx-auto g-text-center g-bg-slate-100 g-opacity-50 g-p-1 g-rounded g-text-sm g-text-slate-800">
                        <FormattedMessage
                          id="taxon.noRecordsWithImages"
                          defaultMessage="No records with images"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="g-flex-auto g-flex g-flex-col g-min-w-0">
                <div className="g-flex-auto">
                  <ArticlePreTitle
                    clickable
                    secondary={
                      taxon.taxonomicStatus === 'DOUBTFUL' ? (
                        <HelpLine
                          id="what-does-the-taxon-status-doubtful-mean-and-when-is-used"
                          icon
                          contentClassName="g-w-auto g-max-w-[min(42rem,100vw)]"
                          title={
                            <span className="">
                              <FormattedMessage
                                id={`enums.taxonomicStatus.${taxon.taxonomicStatus}`}
                              />
                            </span>
                          }
                        />
                      ) : (
                        <FormattedMessage id={`enums.taxonomicStatus.${taxon.taxonomicStatus}`} />
                      )
                    }
                  >
                    <DynamicLink pageId="taxonSearch" searchParams={{ taxonRank: taxon.taxonRank }}>
                      <FormattedMessage
                        id={`enums.taxonRank.${taxon.taxonRank}`}
                        defaultMessage={taxon.taxonRank || ''}
                      />
                    </DynamicLink>
                  </ArticlePreTitle>
                  {/* it would be nice to know for sure which fields to expect */}
                  <ArticleTitle className="lg:g-text-3xl">
                    <span
                      className="g-me-4"
                      dangerouslySetInnerHTML={{
                        __html: taxon?.label || taxon?.scientificName || '',
                      }}
                    />
                  </ArticleTitle>
                  <div>
                    {taxon.acceptedTaxon && (
                      <>
                        <FormattedMessage id="taxon.synonymOf" defaultMessage={'Synonym of'} />
                        <DynamicLink
                          className="hover:g-underline g-ms-2 g-text-primary-500 g-text-inherit"
                          pageId="taxonKey"
                          variables={{ key: taxon?.acceptedTaxon?.taxonID.toString() }}
                        >
                          <span
                            dangerouslySetInnerHTML={{
                              __html:
                                taxon?.acceptedTaxon?.label ||
                                taxon?.acceptedTaxon?.scientificName ||
                                '',
                            }}
                          ></span>
                        </DynamicLink>
                      </>
                    )}

                    {!taxon.acceptedTaxon && taxonInfo.vernacularName && (
                      <div className="g-mb-1">
                        <SimpleTooltip
                          asChild
                          title={
                            <FormattedMessage
                              id="phrases.commonNameAccordingTo"
                              values={{ source: 'Catalogue of Life' }} // TODO taxonapi: if this is no longer a variable, then we can remove the variable
                            />
                          }
                        >
                          <span className="g-text-slate-600 g-inline-flex g-items-center">
                            <span className="g-me-1">
                              {taxonInfo.vernacularName.vernacularName}
                            </span>
                            <MdInfoOutline />
                          </span>
                        </SimpleTooltip>
                      </div>
                    )}

                    {!taxon.acceptedTaxon && taxonInfo?.classification && (
                      // Show the 2 top levels of classification if this is not a synonym. Then ... and then the lowest parent. ... should only show if there is something in between of course. It should be links to the entries
                      <div className="g-text-site-dir-start g-inline-block">
                        <TaxonStubClassification
                          className=""
                          classification={taxonInfo?.classification}
                        />
                      </div>
                      // <Classification className="g-mt-2 g-flex g-flex-wrap g-gap-1 g-items-center">
                      //   {taxonInfo.classification.slice(0, 2).map((c) => (
                      //     <span key={c.taxonID} className="g-flex g-items-center">
                      //       <DynamicLink
                      //         className="hover:g-underline g-text-inherit"
                      //         pageId="taxonKey"
                      //         variables={{ key: c.taxonID.toString() }}
                      //       >
                      //         {c.scientificName}
                      //       </DynamicLink>
                      //     </span>
                      //   ))}
                      //   {taxonInfo.classification.length > 3 && <span>...</span>}
                      //   {taxonInfo.classification.length > 2 && (
                      //     <span className="g-flex g-items-center">
                      //       <DynamicLink
                      //         className="hover:g-underline g-text-inherit"
                      //         pageId="taxonKey"
                      //         variables={{
                      //           key: taxonInfo.classification[
                      //             taxonInfo.classification.length - 1
                      //           ].taxonID.toString(),
                      //         }}
                      //       >
                      //         {
                      //           taxonInfo.classification[taxonInfo.classification.length - 1]
                      //             .scientificName
                      //         }
                      //       </DynamicLink>
                      //     </span>
                      //   )}
                      // </Classification>
                    )}
                  </div>
                </div>
                {/* Mobile-only image — full-width between text and features */}
                {hasOccurrenceImages && (
                  <div className="g-block sm:g-hidden g-mt-4">
                    <HeaderImageCarousel taxon={taxon} />
                  </div>
                )}
                <HeaderInfo className="g-flex-none g-mb-0">
                  <HeaderInfoMain>
                    <FeatureList>
                      {(speciesCount ?? 0) > 0 && (
                        <GenericFeature>
                          <TaxonomyIcon />
                          <DynamicLink
                            pageId="taxonSearch"
                            searchParams={{
                              taxonId: taxon.taxonID,
                              taxonRank: 'SPECIES',
                              taxonomicStatus: 'ACCEPTED',
                            }}
                            className="hover:g-underline g-text-inherit g-whitespace-nowrap"
                          >
                            <FormattedMessage
                              id="counts.nAcceptedSpecies"
                              values={{ total: speciesCount }}
                            />
                          </DynamicLink>
                        </GenericFeature>
                      )}
                      {taxon.relatedInfo?.redlist?.threatStatus && (
                        <GenericFeature>
                          <IucnTag
                            statusCategory={taxon.relatedInfo?.redlist?.threatStatus}
                            as="a"
                            href={
                              taxon.relatedInfo?.redlist?.references ??
                              `https://www.iucnredlist.org/search?query=${taxon.scientificName}&searchType=species`
                            }
                            target="_blank"
                          />
                        </GenericFeature>
                      )}
                      {isIndexedChecklist && isSpeciesOrBelow && (
                        <Cites taxonName={taxon.scientificName} kingdom={kingdom} />
                      )}
                    </FeatureList>
                  </HeaderInfoMain>
                  {isIndexedChecklist && (
                    <HeaderInfoEdit>
                      <Button className="g-mt-4 g-w-full sm:g-w-auto g-whitespace-nowrap">
                        <DynamicLink
                          className="g-text-inherit"
                          pageId="occurrenceSearch"
                          searchParams={{
                            taxonKey: taxon.taxonID.toString(),
                          }}
                        >
                          {countLoading ? (
                            <FormattedMessage id="phrases.loading" />
                          ) : countError ? (
                            <FormattedMessage id="catalogues.occurrences" />
                          ) : (
                            <FormattedMessage id="counts.nOccurrences" values={{ total: count }} />
                          )}
                        </DynamicLink>
                      </Button>
                    </HeaderInfoEdit>
                  )}
                </HeaderInfo>
              </div>
            </div>
            <div className="g-border-b g-mt-4"></div>
            <SectionTabs occurrenceCount={count} />
          </ArticleTextContainer>
        </PageContainer>
        <ErrorBoundary invalidateOn={taxon?.taxonID}>{children}</ErrorBoundary>
      </article>
    </>
  );
};

export const TaxonPageSkeleton = ArticleSkeleton;
